/*
 * Cliente unico de la API del nucleo.
 *
 * Reglas que centraliza, para que ningun modulo las reimplemente:
 *   * La cookie de sesion viaja con credentials: 'include'. El frontend NO
 *     maneja ni almacena token alguno (RNF-009).
 *   * Envoltura comun de respuesta { exito, datos, error, advertencias } (RF-022).
 *   * SESION_REQUERIDA con motivo USUARIO_INACTIVO no se reintenta: lleva a la
 *     pantalla de acceso con el aviso de acceso desactivado (D-09, RN-046c).
 *   * guardarConConexion: sin red no se guarda y no se promete guardar despues
 *     (D-02, RN-034, RN-025).
 */

const BASE = '/api/nucleo';

/**
 * Mensaje de respaldo (RN-348b). Se usa cuando el servidor respondió error pero
 * sin cuerpo utilizable: caída de la puerta de entrada, respuesta que no es la
 * envoltura esperada o tiempo agotado. Texto literal aprobado en §5 del análisis
 * del incremento 08.
 */
export const MENSAJE_DE_RESPALDO =
  'No se pudo completar la operación y el servidor no explicó por qué. '
  + 'Vuelve a intentarlo; si sigue pasando, anota el código de abajo.';

/**
 * Nombres de error del navegador y de los marcos de trabajo. Si el único texto
 * disponible viene de uno de éstos, NO se muestra: rige el respaldo (RN-350).
 */
const TEXTO_DE_PROGRAMADOR = /^(TypeError|ReferenceError|SyntaxError|RangeError|AbortError|NetworkError|Unexpected token)\b/i;

export class ErrorDeApi extends Error {
  constructor({ codigo, mensaje, detalles = [], motivo = null, estado = 0 }) {
    super(mensaje);
    this.name = 'ErrorDeApi';
    this.codigo = codigo;
    // RF-140 · NOMBRE ESTABLE DEL MENSAJE. Causa raíz de H-14 (RN-349): `Error`
    // guarda el texto en `message` y todas las pantallas lo leen como `mensaje`,
    // así que el aviso salía vacío. Se expone con el nombre único que ya usan las
    // pantallas, y NUNCA llega vacío a la vista.
    this.mensaje = mensaje || MENSAJE_DE_RESPALDO;
    this.detalles = detalles;
    this.motivo = motivo;
    this.estado = estado;
  }

  /** Verdadero cuando la sesion se corto por desactivación de la persona (D-09). */
  get esAccesoDesactivado() {
    return this.codigo === 'SESION_REQUERIDA' && this.motivo === 'USUARIO_INACTIVO';
  }
}

/**
 * ÚNICA forma autorizada de obtener el texto de un error para pantalla
 * (RF-140, RN-348). Ninguna pantalla vuelve a leer el campo a mano: el aviso
 * único del núcleo llama aquí.
 *
 * Orden de los tres orígenes del texto (RN-348):
 *   (a) el mensaje que respondió el servidor, tal cual;
 *   (b) el mensaje de respaldo en lenguaje llano si no hubo cuerpo utilizable;
 *   (c) nada, para que el aviso no se dibuje (una franja roja vacía es defecto).
 *
 * Devuelve null SOLO cuando no hay error. Un error siempre tiene texto.
 */
export function mensajeDeError(error) {
  if (!error) return null;

  const propio = typeof error === 'string' ? error : error.mensaje;
  if (typeof propio === 'string' && propio.trim()) return propio;

  // Error del navegador o de un marco de trabajo: su `message` es texto de
  // programador y no se muestra nunca (RN-350) → respaldo.
  const nativo = typeof error.message === 'string' ? error.message.trim() : '';
  if (nativo && !TEXTO_DE_PROGRAMADOR.test(nativo) && !/\b(undefined|null|is not a function)\b/.test(nativo)) {
    return nativo;
  }

  return MENSAJE_DE_RESPALDO;
}

const oyentesDeSesion = new Set();

/** El shell se suscribe para llevar a la pantalla de acceso cuando la sesión termina. */
export function alTerminarLaSesion(llamada) {
  oyentesDeSesion.add(llamada);
  return () => oyentesDeSesion.delete(llamada);
}

function avisarFinDeSesion(error) {
  oyentesDeSesion.forEach((llamada) => llamada(error));
}

async function peticion(ruta, opciones = {}, base = BASE) {
  let respuesta;

  try {
    respuesta = await fetch(`${base}${ruta}`, {
      ...opciones,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(opciones.headers ?? {}),
      },
    });
  } catch {
    // El navegador no pudo salir a la red.
    throw new ErrorDeApi({
      codigo: 'SIN_CONEXION',
      mensaje: 'Necesitas conexión para continuar. Tus datos siguen aquí; inténtalo de nuevo.',
    });
  }

  let cuerpo = null;
  try {
    cuerpo = await respuesta.json();
  } catch {
    cuerpo = null;
  }

  if (respuesta.ok && cuerpo?.exito) return cuerpo.datos;

  const error = new ErrorDeApi({
    codigo: cuerpo?.error?.codigo ?? 'NUCLEO_NO_DISPONIBLE',
    // RN-348b: si el servidor no mandó cuerpo utilizable, el respaldo en lenguaje
    // llano. El texto nunca queda vacío al llegar a la pantalla (RF-140).
    mensaje: cuerpo?.error?.mensaje || MENSAJE_DE_RESPALDO,
    detalles: cuerpo?.error?.detalles ?? [],
    motivo: cuerpo?.error?.motivo ?? null,
    estado: respuesta.status,
  });

  if (error.codigo === 'SESION_REQUERIDA') avisarFinDeSesion(error);

  throw error;
}

export const api = crearCliente(BASE);

/**
 * Cliente para OTRA base de rutas, con EXACTAMENTE las mismas reglas: cookie de
 * sesión, envoltura común y aviso de fin de sesión al shell.
 *
 * Existe para que los módulos 2 a 6 —que cuelgan de /api/cuentas,
 * /api/movimientos…— no reimplementen esas tres reglas por su cuenta, que es
 * justo lo que este archivo evita. No cambia el comportamiento de `api`: `api`
 * es ahora una instancia de esta misma fábrica.
 */
export function crearCliente(base) {
  return {
    obtener: (ruta) => peticion(ruta, { method: 'GET' }, base),
    publicar: (ruta, cuerpo) => peticion(ruta, { method: 'POST', body: JSON.stringify(cuerpo ?? {}) }, base),
    actualizar: (ruta, cuerpo) => peticion(ruta, { method: 'PUT', body: JSON.stringify(cuerpo ?? {}) }, base),
    eliminar: (ruta) => peticion(ruta, { method: 'DELETE' }, base),
  };
}

/**
 * Contrato ExigirConexionParaGuardar (D-02). Se usa en CADA boton de guardar de
 * cualquier modulo: centraliza "sin red no se guarda" para que nadie invente una
 * cola de sincronizacion propia. Nunca devuelve exito optimista.
 */
export async function guardarConConexion(ejecutar) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new ErrorDeApi({
      codigo: 'SIN_CONEXION',
      mensaje: 'Necesitas conexión para guardar. Tus datos siguen aquí; inténtalo de nuevo.',
    });
  }

  return ejecutar();
}
